import type { PropsWithChildren, ReactNode} from 'react';
import { useState } from 'react';

import * as RadixTooltip from '@radix-ui/react-tooltip';
import { cn } from '@ui/uikit/utils';

import { Overlay } from '../Overlays';

export type TooltipContentProps<T = ReactNode> =
  | { caption: T }
  | { title: T; message?: T }
  | { message: T; title?: T };

type PortalMode = 'none' | 'default' | 'overlay';

export type TooltipProps<T = ReactNode> = Pick<RadixTooltip.TooltipContentProps, 'sideOffset'> & {
  contentClassName?: string;
  trigger: ReactNode;
  side?: 'bottom' | 'top' | 'left' | 'right';
  portalMode?: PortalMode;
} & TooltipContentProps<T>;

const TooltipContentPortal = ({
  mode: portalMode,
  children,
}: PropsWithChildren<{ mode: PortalMode }>) => {
  switch (portalMode) {
    case 'none':
      return children;

    case 'default':
      return <RadixTooltip.Portal>{children}</RadixTooltip.Portal>;

    case 'overlay':
      return <Overlay>{children}</Overlay>;
  }
};

export const Tooltip = ({
  contentClassName,
  sideOffset = 4,
  trigger,
  side = 'bottom',
  portalMode = 'default',
  ...props
}: TooltipProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const isCaption = 'caption' in props && !!props.caption;
  const isMessage = ('message' in props && !!props.message) || ('title' in props && !!props.title);

  return (
    <RadixTooltip.Provider>
      <RadixTooltip.Root open={isOpen}>
        <RadixTooltip.Trigger
          onMouseEnter={() => {
            setIsOpen(true);
          }}
          onMouseLeave={() => {
            setIsOpen(false);
          }}
          onFocus={() => {
            setIsOpen(true);
          }}
          onBlur={() => {
            setIsOpen(false);
          }}
          asChild
        >
          {trigger}
        </RadixTooltip.Trigger>
        <TooltipContentPortal mode={portalMode}>
          <RadixTooltip.Content
            className={cn(
              contentClassName,
              isCaption ? 'tooltip-caption-a' : 'tooltip-message-a',
              'tooltip-animate',
            )}
            sideOffset={sideOffset}
            side={side}
            alignOffset={-29}
            collisionPadding={{ left: -13 }}
          >
            {isCaption && props.caption}

            {isMessage && (
              <>
                {props.title && <h5 className="tooltip-message-title">{props.title}</h5>}
                {props.message && <p>{props.message}</p>}
              </>
            )}
          </RadixTooltip.Content>
        </TooltipContentPortal>
      </RadixTooltip.Root>
    </RadixTooltip.Provider>
  );
};
